import { Avatar, Box, Typography, keyframes } from '@mui/material';
import PropTypes from 'prop-types';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(28px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const countPop = keyframes`
  0% { transform: scale(0.8); opacity: 0; }
  60% { transform: scale(1.06); }
  100% { transform: scale(1); opacity: 1; }
`;

const shineSweep = keyframes`
  0% { transform: translateX(-120%) skewX(-12deg); opacity: 0; }
  15% { opacity: 0.5; }
  100% { transform: translateX(220%) skewX(-12deg); opacity: 0; }
`;

const orbPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.4); opacity: 1; }
`;

const glassBase = accent => ({
  background: `linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.55) 0%,
    rgba(255, 255, 255, 0.28) 50%,
    ${accent}12 100%
  )`,
  backdropFilter: 'blur(18px) saturate(200%)',
  WebkitBackdropFilter: 'blur(18px) saturate(200%)',
  border: '1px solid rgba(255, 255, 255, 0.55)',
  boxShadow: `
    0 8px 32px rgba(31, 41, 51, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.75),
    inset 0 -1px 0 rgba(255, 255, 255, 0.15)
  `
});

function DashboardMetricCard({
  count,
  description,
  icon,
  accent,
  index = 0,
  loading = false
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: { xs: 140, sm: 160, lg: 'calc((100vh - 220px) / 2 - 14px)' },
        borderRadius: '24px',
        p: { xs: 2.5, md: 3, lg: 3.5 },
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: { xs: 2, md: 3 },
        boxSizing: 'border-box',
        ...glassBase(accent),
        overflow: 'hidden',
        cursor: 'pointer',
        animation: `${fadeInUp} 0.65s cubic-bezier(0.22, 1, 0.36, 1) both`,
        animationDelay: `${index * 0.09}s`,
        transition:
          'transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.45s ease, border-color 0.35s ease, background 0.4s ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          background: `linear-gradient(180deg, ${accent} 0%, ${accent}66 100%)`,
          borderRadius: '24px 0 0 24px',
          opacity: 0.85,
          transition: 'width 0.4s ease, opacity 0.4s ease'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          right: -50,
          top: -50,
          width: 160,
          height: 160,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${accent}35 0%, transparent 65%)`,
          pointerEvents: 'none',
          transition: 'transform 0.5s ease, opacity 0.5s ease'
        },
        '& .metric-shine': {
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          borderRadius: '24px',
          pointerEvents: 'none',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '50%',
            height: '100%',
            background:
              'linear-gradient(90deg, transparent, rgba(255,255,255,0.65), transparent)',
            transform: 'translateX(-120%) skewX(-12deg)',
            opacity: 0
          }
        },
        '&:hover': {
          transform: 'translateY(-12px) scale(1.025)',
          background: `linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.72) 0%,
            rgba(255, 255, 255, 0.42) 45%,
            ${accent}22 100%
          )`,
          borderColor: 'rgba(255, 255, 255, 0.85)',
          boxShadow: `
            0 28px 56px ${accent}30,
            0 12px 24px rgba(31, 41, 51, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.95),
            inset 0 0 40px ${accent}15
          `,
          '&::before': {
            width: '6px',
            opacity: 1
          },
          '&::after': {
            transform: 'scale(1.5)',
            opacity: 1,
            animation: `${orbPulse} 2s ease-in-out infinite`
          },
          '& .metric-shine::after': {
            animation: `${shineSweep} 0.8s ease-out forwards`
          },
          '& .metric-glass-icon': {
            transform: 'scale(1.1) rotate(-3deg)',
            background: `rgba(255, 255, 255, 0.65)`,
            boxShadow: `0 8px 28px ${accent}35`,
            borderColor: `${accent}50`
          },
          '& .metric-count': {
            color: accent,
            transform: 'scale(1.04)',
            textShadow: `0 0 40px ${accent}40`
          },
          '& .metric-label': {
            color: '#1F2933'
          }
        },
        '&:active': {
          transform: 'translateY(-6px) scale(1.015)',
          transitionDuration: '0.12s'
        }
      }}
    >
      <Box className="metric-shine" />
      <Avatar
        className="metric-glass-icon"
        src={icon}
        variant="rounded"
        sx={{
          width: { xs: 56, lg: 64 },
          height: { xs: 56, lg: 64 },
          borderRadius: '18px',
          bgcolor: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: `1.5px solid ${accent}35`,
          flexShrink: 0,
          zIndex: 1,
          transition:
            'transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.45s ease, background 0.35s ease, border-color 0.35s ease'
        }}
      />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          width: '100%'
        }}
      >
        <Typography
          className="metric-count"
          sx={{
            fontSize: { xs: '2rem', sm: '2.5rem', lg: '2.85rem', xl: '3.1rem' },
            fontWeight: 800,
            lineHeight: 1,
            letterSpacing: '-0.03em',
            color: '#1F2933',
            fontVariantNumeric: 'tabular-nums',
            transition:
              'color 0.35s ease, transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1), text-shadow 0.35s ease',
            animation: loading ? 'none' : `${countPop} 0.55s ease-out both`,
            animationDelay: `${index * 0.09 + 0.2}s`
          }}
        >
          {loading ? '—' : Number(count).toLocaleString('en-IN')}
        </Typography>
        <Typography
          className="metric-label"
          sx={{
            mt: { xs: 0.75, lg: 1.25 },
            fontWeight: 600,
            fontSize: { xs: '0.9rem', lg: '1rem' },
            color: '#4E565F',
            lineHeight: 1.35,
            transition: 'color 0.35s ease'
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

DashboardMetricCard.propTypes = {
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  description: PropTypes.string.isRequired,
  icon: PropTypes.string,
  accent: PropTypes.string.isRequired,
  index: PropTypes.number,
  loading: PropTypes.bool
};

export default DashboardMetricCard;
